import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, GripVertical } from 'lucide-react';

// Column manager popover shared by the Screener, Ward and Referrals tables.
// Lists every column with a drag handle (reorder) and a checkbox (show / hide),
// plus a Reset. Resize is done on the table headers themselves; this menu is the
// only place to hide / re-show a column. Anchored under the toolbar "Columns"
// button; closes on click-away or Escape. Generic over the panel's column-id.

interface MenuActions<Id extends string> {
  moveColumn: (id: Id, targetIndex: number) => void;
  setColumnVisible: (id: Id, visible: boolean) => void;
  resetToDefaults: () => void;
}

export function ColumnsMenu<Id extends string>({
  open,
  onClose,
  anchorRef,
  columns,
  label,
  actions,
}: {
  open: boolean;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement | null>;
  columns: ReadonlyArray<{ id: Id; visible: boolean }>;
  label: (id: Id) => string;
  actions: MenuActions<Id>;
}) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [dragId, setDragId] = useState<Id | null>(null);
  const [overId, setOverId] = useState<Id | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    function onDown(e: MouseEvent) {
      const target = e.target as Node;
      if (menuRef.current?.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      onClose();
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open, onClose, anchorRef]);

  if (!open || !anchorRef.current) return null;
  const rect = anchorRef.current.getBoundingClientRect();
  const visibleCount = columns.filter((c) => c.visible).length;

  function onDrop(targetId: Id) {
    if (!dragId || dragId === targetId) return;
    const fromIndex = columns.findIndex((c) => c.id === dragId);
    const targetIndex = columns.findIndex((c) => c.id === targetId);
    if (fromIndex === -1 || targetIndex === -1) return;
    const adjusted = fromIndex < targetIndex ? targetIndex - 1 : targetIndex;
    actions.moveColumn(dragId, adjusted);
    setDragId(null);
    setOverId(null);
  }

  return createPortal(
    <div
      ref={menuRef}
      role="menu"
      aria-label="Columns"
      style={{
        position: 'fixed',
        top: rect.bottom + 4,
        left: rect.left,
        minWidth: 224,
        background: 'var(--bg)',
        border: '1px solid var(--hairline)',
        borderRadius: 2,
        boxShadow: '0 6px 24px rgba(10,14,26,0.14), 0 2px 6px rgba(10,14,26,0.08)',
        zIndex: 9999,
        padding: 'var(--s-1)',
        fontSize: 'var(--t-body)',
      }}
    >
      <div
        style={{
          fontSize: 'var(--t-meta)',
          color: 'var(--text-tertiary)',
          padding: '4px 6px 6px',
        }}
      >
        Columns · drag to reorder
      </div>
      {columns.map((col) => {
        const lastVisible = col.visible && visibleCount <= 1;
        return (
          <div
            key={col.id}
            draggable
            onDragStart={(e) => {
              e.dataTransfer.effectAllowed = 'move';
              setDragId(col.id);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              if (col.id !== overId) setOverId(col.id);
            }}
            onDragLeave={() => setOverId((id) => (id === col.id ? null : id))}
            onDrop={(e) => {
              e.preventDefault();
              onDrop(col.id);
            }}
            onDragEnd={() => {
              setDragId(null);
              setOverId(null);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '5px 6px',
              borderRadius: 2,
              cursor: 'grab',
              opacity: dragId === col.id ? 0.4 : 1,
              borderTop:
                overId === col.id && dragId && dragId !== col.id
                  ? '2px solid var(--accent)'
                  : '2px solid transparent',
            }}
          >
            <GripVertical size={14} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
            <button
              type="button"
              role="checkbox"
              aria-checked={col.visible}
              disabled={lastVisible}
              onClick={(e) => {
                e.stopPropagation();
                actions.setColumnVisible(col.id, !col.visible);
              }}
              title={lastVisible ? 'At least one column must stay visible' : undefined}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 14,
                height: 14,
                flexShrink: 0,
                padding: 0,
                borderRadius: 2,
                border: `1px solid ${col.visible ? 'var(--accent)' : 'var(--hairline)'}`,
                background: col.visible ? 'var(--accent)' : 'var(--surface)',
                color: 'var(--bg)',
                cursor: lastVisible ? 'not-allowed' : 'pointer',
              }}
            >
              {col.visible && <Check size={10} strokeWidth={3} />}
            </button>
            <span
              style={{
                color: col.visible ? 'var(--text-primary)' : 'var(--text-tertiary)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {label(col.id)}
            </span>
          </div>
        );
      })}
      <div
        style={{
          borderTop: '1px solid var(--hairline)',
          marginTop: 'var(--s-1)',
          paddingTop: 'var(--s-1)',
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <button
          type="button"
          onClick={() => actions.resetToDefaults()}
          style={{
            font: 'inherit',
            fontSize: 'var(--t-meta)',
            color: 'var(--text-secondary)',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '2px 6px',
          }}
        >
          Reset
        </button>
      </div>
    </div>,
    document.body,
  );
}
